import { CA_AREA_CODES, PROVINCE_NAMES } from '@/config/caAreaCodes'
import { STATE_NAMES, US_AREA_CODES } from '@/config/usAreaCodes'

const regionList = names => Object.entries(names).map(([code, name]) => ({ code, name }))

export const DEFAULT_SELLING_COUNTRY = 'USA'

export const SELLING_COUNTRIES = [
  {
    code: 'USA',
    name: 'United States',
    shortName: 'US',
    currency: 'USD',
    currencySymbol: '$',
    setupNote: 'Prices in US dollars, with a local US number for texting customers.',
    numberNote: null,
    phoneNumbers: true,
    areaCodes: US_AREA_CODES,
    areaCodeSearchExample: 'Try "Austin", "TX", or "512"...',
    regionNoun: 'state',
    mapFocus: null,
    address: {
      regionLabel: 'State',
      regionPlaceholder: 'IL or Illinois',
      regions: regionList(STATE_NAMES),
      placesCountry: 'us',
      postalLabel: 'ZIP',
      postalPlaceholder: '60612',
      postalMaxlength: 10,
      streetPlaceholder: '1750 W Fulton Market',
      cityPlaceholder: 'Chicago'
    }
  },
  {
    code: 'CAN',
    name: 'Canada',
    shortName: 'Canada',
    currency: 'CAD',
    currencySymbol: '$',
    setupNote: 'Prices in Canadian dollars, with a local Canadian number for texting customers.',
    numberNote: null,
    phoneNumbers: true,
    areaCodes: CA_AREA_CODES,
    areaCodeSearchExample: 'Try "Toronto", "ON", or "416"...',
    regionNoun: 'province',
    mapFocus: [43.6532, -79.3832],
    address: {
      regionLabel: 'Province',
      regionPlaceholder: 'ON or Ontario',
      regions: regionList(PROVINCE_NAMES),
      placesCountry: 'ca',
      postalLabel: 'Postal code',
      postalPlaceholder: 'M5V 2T6',
      postalMaxlength: 7,
      streetPlaceholder: '220 King St W',
      cityPlaceholder: 'Toronto'
    }
  },
  {
    code: 'GBR',
    name: 'United Kingdom',
    shortName: 'UK',
    currency: 'GBP',
    currencySymbol: '£',
    setupNote: 'Prices in pounds, and our team adds your UK number for texting customers after signup.',
    numberNote: 'Our team adds your UK number',
    phoneNumbers: false,
    areaCodes: [],
    areaCodeSearchExample: '',
    regionNoun: 'county',
    mapFocus: [51.5072, -0.1276],
    address: {
      regionLabel: 'County',
      regionPlaceholder: 'Greater London',
      regions: null,
      placesCountry: 'gb',
      postalLabel: 'Postcode',
      postalPlaceholder: 'SE1 9RA',
      postalMaxlength: 8,
      streetPlaceholder: '12 Borough High St',
      cityPlaceholder: 'London'
    }
  }
]

export const AREA_CODE_REGION_NAMES = { ...STATE_NAMES, ...PROVINCE_NAMES }

export function sellingCountry(code) {
  return SELLING_COUNTRIES.find(country => country.code === code) || SELLING_COUNTRIES[0]
}

export function currencySymbolFor(currency) {
  return (SELLING_COUNTRIES.find(country => country.currency === currency) || SELLING_COUNTRIES[0]).currencySymbol
}

export function stepApplies(step, data = {}) {
  return !step?.needsPhoneNumber || sellingCountry(data?.country).phoneNumbers
}

export function countryChangePatch(data = {}, country) {
  const previous = data.country || DEFAULT_SELLING_COUNTRY
  if (previous === country || !data.area_code) return { country }
  return { country, area_code: '', area_city: '' }
}

export function needsBackgroundScan(data = {}, { country, website_url: websiteUrl } = {}) {
  if (!websiteUrl || sellingCountry(country).phoneNumbers) return false
  const scanned = data.manual_fallback !== undefined
  const sameSite = data.website_url === websiteUrl
  const sameCountry = (data.country || DEFAULT_SELLING_COUNTRY) === country
  return !(scanned && sameSite && sameCountry)
}

export function mapFocusFor(countryCode, areaCode) {
  const country = sellingCountry(countryCode)
  const row = country.areaCodes.find(entry => entry.area_code === areaCode)
  return row ? [row.lat, row.lng] : country.mapFocus
}

export function areaCodeSearchCopy(countryCode, { found = false, scanned = true } = {}) {
  const { regionNoun, areaCodeSearchExample } = sellingCountry(countryCode)
  return {
    placeholder: areaCodeSearchExample,
    subtitle:
      found || !scanned
        ? `Search by city, ${regionNoun} or code - your customers will see a familiar local number.`
        : `We couldn't automatically find your area code - search by city, ${regionNoun} or code to pick a familiar local number.`,
    empty: query =>
      query
        ? `No matches - try a city, ${regionNoun}, or area code.`
        : `Start with your city, ${regionNoun}, or area code.`
  }
}
